// =============================================================================
// BASE CAMPAIGN FORM - Shared form for donation/petition campaign creation
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp } from '../../utils/animations';
import { createCampaign, checkSlugAvailable } from '../../services/api';
import { useDebounce } from '../../hooks';
import type { CampaignType } from '../../types';

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

interface BaseCampaignFormProps {
  onBack: () => void;
  campaignType: CampaignType;
  ariaLabel: string;
  headerTitle: string;
  headerSubtitle: string;
  terminalLabel: string;
  urlLabel: string;
  urlPlaceholder: string;
  urlMissingError: string;
  urlInvalidError: string;
  urlDomainError: string;
  urlDomainCheck: (url: string) => boolean;
  titleLabel: string;
  titlePlaceholder: string;
  defaultTitle: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  footerText: string;
}

export function BaseCampaignForm({
  onBack,
  campaignType,
  ariaLabel,
  headerTitle,
  headerSubtitle,
  terminalLabel,
  urlLabel,
  urlPlaceholder,
  urlMissingError,
  urlInvalidError,
  urlDomainError,
  urlDomainCheck,
  titleLabel,
  titlePlaceholder,
  defaultTitle,
  descriptionLabel,
  descriptionPlaceholder,
  footerText,
}: Readonly<BaseCampaignFormProps>) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [generatedLink, setGeneratedLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSlug = useDebounce(slug, 400);

  const isDonation = campaignType === 'donation';
  const accentText = isDonation ? 'text-war-primary' : 'text-war-amber';
  const accentBorder = isDonation ? 'border-war-primary' : 'border-war-amber';
  const accentBorderMuted = isDonation
    ? 'border-war-primary/30'
    : 'border-war-amber/30';
  const accentBg = isDonation ? 'bg-war-primary/10' : 'bg-war-amber/10';
  const accentHoverBg = isDonation
    ? 'hover:bg-war-primary/20'
    : 'hover:bg-war-amber/20';
  const accentFocus = isDonation
    ? 'focus:border-war-primary'
    : 'focus:border-war-amber';
  const accentPulse = isDonation ? 'bg-war-primary' : 'bg-war-amber';
  const accentCopyActive = isDonation
    ? 'bg-war-primary/20 border-war-primary text-war-primary'
    : 'bg-war-amber/20 border-war-amber text-war-amber';

  useEffect(() => {
    if (!debouncedSlug || debouncedSlug.length < 3) {
      setSlugStatus('idle');
      return;
    }

    const checkAvailability = async () => {
      setSlugStatus('checking');
      const result = await checkSlugAvailable(debouncedSlug);
      if (result.reason === 'invalid_format') {
        setSlugStatus('invalid');
      } else if (result.available) {
        setSlugStatus('available');
      } else {
        setSlugStatus('taken');
      }
    };

    checkAvailability();
  }, [debouncedSlug]);

  const handleSlugChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setSlug(value);
    },
    [],
  );

  const handleGenerate = async () => {
    setError(null);
    setIsLoading(true);

    if (!url) {
      setError(urlMissingError);
      setIsLoading(false);
      return;
    }

    try {
      new URL(url);
    } catch {
      setError(urlInvalidError);
      setIsLoading(false);
      return;
    }

    if (!urlDomainCheck(url)) {
      setError(urlDomainError);
      setIsLoading(false);
      return;
    }

    if (!slug || slug.length < 3) {
      setError('Введіть назву організації (мінімум 3 символи)');
      setIsLoading(false);
      return;
    }

    if (slugStatus !== 'available') {
      setError('Ця назва недоступна. Оберіть іншу.');
      setIsLoading(false);
      return;
    }

    try {
      const result = await createCampaign({
        url,
        title: title || defaultTitle,
        description: description || undefined,
        slug,
        type: campaignType,
      });

      setGeneratedLink(`https://${result.slug}.holovkom.zavoruha.tech`);
    } catch (err) {
      console.error('Error creating campaign:', err);
      if (err instanceof Error && err.message.includes('SLUG_TAKEN')) {
        setError('Ця назва вже зайнята. Оберіть іншу.');
      } else {
        setError('Не вдалося створити посилання. Спробуйте пізніше.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const getSlugStatusIcon = () => {
    switch (slugStatus) {
      case 'checking':
        return <span className="text-war-dim animate-pulse">⏳</span>;
      case 'available':
        return <span className={accentText}>✓</span>;
      case 'taken':
        return <span className="text-war-red">✗</span>;
      case 'invalid':
        return <span className={accentText}>⚠</span>;
      default:
        return null;
    }
  };

  const getSlugStatusText = () => {
    switch (slugStatus) {
      case 'checking':
        return 'Перевіряю...';
      case 'available':
        return 'Доступно!';
      case 'taken':
        return 'Вже зайнято';
      case 'invalid':
        return 'Невірний формат';
      default:
        return '';
    }
  };

  let slugStatusTextClass = 'text-war-dim/60';
  if (slugStatus === 'available') {
    slugStatusTextClass = accentText;
  } else if (slugStatus === 'taken' || slugStatus === 'invalid') {
    slugStatusTextClass = 'text-war-red';
  }

  const slugInputId = 'campaign-slug';
  const urlInputId = 'campaign-url';
  const titleInputId = 'campaign-title';
  const descriptionInputId = 'campaign-description';

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8 relative"
      role="main"
      aria-label={ariaLabel}
    >
      <motion.div
        className="relative z-10 w-full max-w-lg"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.1 },
          },
        }}
      >
        <motion.div variants={fadeInUp} className="text-center mb-8">
          <button
            onClick={onBack}
            className="text-war-dim hover:text-war-primary text-xs mb-6 flex items-center gap-2 transition-colors mx-auto"
            aria-label="Повернутися назад"
          >
            <span>←</span>
            <span className="tracking-wider uppercase">Назад</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-war-bright tracking-wider uppercase mb-3">
            {headerTitle}
          </h1>
          <p className="text-war-dim text-xs font-mono uppercase tracking-wide">
            {headerSubtitle}
          </p>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          className="glass-card p-6 md:p-8 space-y-5"
        >
          <div className="flex items-center gap-2 pb-4 border-b border-war-dim/20">
            <div className="w-2 h-2 rounded-full bg-war-red/70" />
            <div className="w-2 h-2 rounded-full bg-war-amber/70" />
            <div className="w-2 h-2 rounded-full bg-war-primary/70" />
            <span className="ml-2 text-war-dim text-[9px] tracking-wider uppercase font-mono">
              {terminalLabel}
            </span>
          </div>

          <div>
            <label
              htmlFor={slugInputId}
              className={`block ${accentText} text-[10px] font-mono uppercase tracking-widest mb-2`}
            >
              Назва організації <span className="text-war-red">*</span>
            </label>
            <div className="relative">
              <input
                id={slugInputId}
                type="text"
                value={slug}
                onChange={handleSlugChange}
                placeholder="moya-brigada"
                maxLength={30}
                className={`w-full bg-war-bg/50 border border-war-dim/30 px-4 py-3 text-war-bright text-sm font-mono ${accentFocus} focus:outline-none transition-colors placeholder:text-war-dim/40 pr-10`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {getSlugStatusIcon()}
              </div>
            </div>
            <div className="flex justify-between items-center mt-1">
              <p className="text-[10px] text-war-dim/60 font-mono">
                {slug && `${slug}.holovkom.zavoruha.tech`}
              </p>
              <p className={`text-[10px] font-mono ${slugStatusTextClass}`}>
                {getSlugStatusText()}
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor={urlInputId}
              className={`block ${accentText} text-[10px] font-mono uppercase tracking-widest mb-2`}
            >
              {urlLabel} <span className="text-war-red">*</span>
            </label>
            <input
              id={urlInputId}
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={urlPlaceholder}
              className={`w-full bg-war-bg/50 border border-war-dim/30 px-4 py-3 text-war-bright text-sm font-mono ${accentFocus} focus:outline-none transition-colors placeholder:text-war-dim/40`}
            />
          </div>

          <div>
            <label
              htmlFor={titleInputId}
              className={`block ${accentText} text-[10px] font-mono uppercase tracking-widest mb-2`}
            >
              {titleLabel}
            </label>
            <input
              id={titleInputId}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={titlePlaceholder}
              maxLength={50}
              className={`w-full bg-war-bg/50 border border-war-dim/30 px-4 py-3 text-war-bright text-sm font-mono ${accentFocus} focus:outline-none transition-colors placeholder:text-war-dim/40`}
            />
          </div>

          <div>
            <label
              htmlFor={descriptionInputId}
              className={`block ${accentText} text-[10px] font-mono uppercase tracking-widest mb-2`}
            >
              {descriptionLabel}
            </label>
            <textarea
              id={descriptionInputId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={descriptionPlaceholder}
              maxLength={150}
              className={`w-full bg-war-bg/50 border border-war-dim/30 px-4 py-3 text-war-bright text-sm font-mono ${accentFocus} focus:outline-none transition-colors placeholder:text-war-dim/40 h-20 resize-none`}
            />
          </div>

          {error && (
            <div className="text-war-red text-xs font-mono bg-war-red/10 border border-war-red/30 p-3">
              ⚠ {error}
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={isLoading || slugStatus !== 'available'}
            className={`w-full group relative ${accentBg} border-2 ${accentBorder} ${accentHoverBg} transition-all py-4 px-6 active:scale-[0.98] ${
              isLoading || slugStatus !== 'available'
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            <span
              className={`${accentText} group-hover:text-war-bright text-sm font-bold tracking-widest uppercase transition-colors`}
            >
              {isLoading ? 'Генерую...' : 'Згенерувати посилання'}
            </span>
            <div
              className={`absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 ${accentBorder}`}
            />
            <div
              className={`absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 ${accentBorder}`}
            />
            <div
              className={`absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 ${accentBorder}`}
            />
            <div
              className={`absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 ${accentBorder}`}
            />
          </button>
        </motion.div>

        {generatedLink && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-6 glass-card p-5 ${accentBorderMuted}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-2 h-2 ${accentPulse} animate-pulse`} />
              <span
                className={`${accentText} text-[10px] font-mono uppercase tracking-widest`}
              >
                Посилання готове
              </span>
            </div>
            <div className="flex gap-2">
              <input
                readOnly
                value={generatedLink}
                className="flex-1 bg-war-bg/50 border border-war-dim/30 px-3 py-2 text-xs text-war-bright font-mono"
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all border ${
                  copied
                    ? accentCopyActive
                    : 'bg-war-panel border-war-dim/30 text-war-dim hover:text-war-bright hover:border-war-dim'
                }`}
              >
                {copied ? '✓' : 'Копіювати'}
              </button>
            </div>
          </motion.div>
        )}

        <motion.div
          variants={fadeInUp}
          className="mt-8 text-center text-[9px] text-war-dim/40 font-mono tracking-wide"
        >
          {footerText}
        </motion.div>
      </motion.div>
    </main>
  );
}
